import { createGlobalState } from '@vueuse/core';
import { ref, watch, type Ref } from 'vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useSearchRequest } from '~/composables/useSearchRequest';
import { outboundMessagesAnalyticsClient } from '~/services';
import type {
    OutboundMessagesParams,
    Resource,
} from '~/services/analytics/outboundMessages/outboundMessagesAnalyticsClient.types';
import type { ResourceType } from '~/utils/analytics';

export type SelectedResource = Resource | { resource_type: ResourceType; resource_id: number };

export const useAnalyticsSelectResources = createGlobalState(() => {
    const teamId = Spark.state.currentTeam.id;
    const { getCurrentGroupId, currentOrgUnit } = useCurrentGroup();

    // used to retrieve the name of resources
    const cachedResources: Ref<Resource[]> = ref([]);

    const selectingResources = ref(false);
    const selectedResources: Ref<SelectedResource[]> = ref([]);

    watch(
        selectedResources,
        () => {
            cachedResources.value = cachedResources.value.concat(
                // whenever selected resources is changed update the cache
                selectedResources.value.filter(
                    (selected): selected is Resource =>
                        'resource_name' in selected &&
                        Boolean(selected.resource_name) &&
                        !cachedResources.value.some((cached) => isSameResource(selected, cached))
                )
            );
        },
        {
            deep: true,
        }
    );

    const {
        loading: loadingOptions,
        response: options,
        createSearch,
        cancelKey,
    } = useSearchRequest<Resource[]>(undefined, 'analytics-resources-search');

    // TODO: Add default value to useSearchRequest, make the first argument of useSearchRequest be an object with keys, instead of multiple arguments.
    options.value = [];

    const fetchSelectedResources = async (fetchParams: OutboundMessagesParams): Promise<void> => {
        const missingResources = selectedResources.value
            .filter((selected) => !cachedResources.value.some((cached) => isSameResource(selected, cached)))
            .map(({ resource_type, resource_id }) => ({
                resource_type,
                resource_id,
            }));

        if (missingResources.length === 0) {
            return;
        }

        const params = {
            range: fetchParams.range,
            filters: {
                resources: missingResources,
            },
        };

        const response = await outboundMessagesAnalyticsClient.getListActiveResources(
            {
                teamId,
                orgUnitId: currentOrgUnit.value?.id,
                groupId: getCurrentGroupId(),
            },
            params
        );

        cachedResources.value = cachedResources.value.concat(response.data.data);
    };

    const searchFn = createSearch(async (fetchParams: OutboundMessagesParams, query: string) => {
        const params = {
            ...fetchParams,
            filters: {
                ...fetchParams.filters,
                resources: undefined,
                snippet_ids: undefined,
            },
            query: query,
        };

        const response = await outboundMessagesAnalyticsClient.getListActiveResources(
            {
                teamId,
                orgUnitId: currentOrgUnit.value?.id,
                groupId: getCurrentGroupId(),
            },
            params,
            { cancelKey }
        );

        if (!query) {
            fetchSelectedResources(fetchParams);
        }

        return response.data.data;
    });

    const isSameResource = (a: SelectedResource, b: SelectedResource): boolean =>
        a.resource_type === b.resource_type && a.resource_id === b.resource_id;

    const resourceName = (resource: SelectedResource): string | undefined =>
        cachedResources.value.find((option) => isSameResource(resource, option))?.resource_name;

    return {
        selectingResources,
        selectedResources,
        loadingOptions,
        options,
        searchFn,
        isSameResource,
        resourceName,
    };
});
